/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ChangeEvent, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, TextField, Typography2 } from '@coursera/cds-core';

import {
  cancelTwoFactorAuthenticationAction,
  confirmTwoFactorAuthenticationCode,
} from 'bundles/account-settings/actions/SettingsActionCreators';

import _t from 'i18n!nls/account-settings';

type Props = {
  qrCode: string;
  invalidFields: string[];
  updating: boolean;
  onClose?: () => void;
  returnFocusRef?: RefObject<HTMLElement>;
};

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
  `,
  paragraph: css`
    && {
      margin: 0;
    }

    a {
      text-decoration: underline;
    }
  `,
  qrSlot: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding-right: var(--cds-spacing-300);
  `,
  qrImage: css`
    width: 160px;
    height: 160px;
  `,
};

const CodeConfirmationModal = ({ qrCode, invalidFields, updating, onClose, returnFocusRef }: Props) => {
  const [code, setCode] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const hasCodeError = !dismissed && invalidFields.includes('code');

  useEffect(() => {
    if (hasCodeError) {
      codeInputRef.current?.focus();
    }
  }, [hasCodeError]);

  const handleSubmit = () => {
    setDismissed(false);
    confirmTwoFactorAuthenticationCode(code);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value.trim());
    setDismissed(true);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      cancelTwoFactorAuthenticationAction();
    }
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      initialFocusRef={codeInputRef}
      returnFocusRef={returnFocusRef}
      slotContent={
        <div css={styles.qrSlot}>
          <img
            css={styles.qrImage}
            src={qrCode}
            alt={_t('QR Code. Scan with a two-factor authentication mobile app to enable two factor authentication')}
          />
        </div>
      }
      slotOrientation="horizontalEnd"
    >
      <Dialog.HeadingGroup>{_t('Enable Two-Factor Auth')}</Dialog.HeadingGroup>
      <Dialog.Content>
        <div css={styles.content}>
          <Typography2 variant="bodyPrimary" component="p" css={styles.paragraph}>
            <FormattedHTMLMessage
              message={_t(
                `Using your favorite two-factor authentication mobile app (we recommend
                    <a href="https://support.google.com/accounts/answer/1066447"
                       target="_blank" rel="noopener noreferrer">Google Authenticator</a>),
                    scan the barcode displayed on the screen.`
              )}
            />
          </Typography2>
          <Typography2 variant="bodyPrimary" component="p" css={styles.paragraph}>
            <FormattedHTMLMessage
              message={_t(
                `Once your app recognizes your Coursera account,
                    enter the code it shows for your account and click "Verify Code"`
              )}
            />
          </Typography2>
          <TextField
            inputRef={codeInputRef}
            id="settings-two-factor-code"
            label={_t('Two-factor code')}
            placeholder="123456"
            value={code}
            onChange={handleChange}
            fullWidth
            validationStatus={hasCodeError ? 'error' : undefined}
            validationLabel={hasCodeError ? _t('Your two-factor code did not match. Please try again.') : undefined}
          />
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button variant="primary" onClick={handleSubmit} disabled={updating} type="button" fullWidth>
          {_t('Verify Code')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export default CodeConfirmationModal;
